import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  User as FirebaseUser
} from 'firebase/auth'
import { app } from '../services/firebase'

interface User {
  id: string
  email: string
  name: string | null
  organization: string | null
  role: 'admin' | 'partner' | 'consultant' | 'collaborator' | 'subcontractor' | 'client'
}

interface AuthContextValue {
  user: User | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  error: string | null
  needsProfile: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  signup: (email: string, password: string, firstName: string, lastName: string, organization: string) => Promise<void>
  completeProfile: (firstName: string, lastName: string, organization: string) => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Pending signup info stored between Firebase create and API register
let pendingSignupInfo: { firstName: string; lastName: string; organization: string } | null = null

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [needsProfile, setNeedsProfile] = useState(false)

  const auth = getAuth(app)

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)

      // Always set loading true when auth state changes
      setLoading(true)

      if (fbUser) {
        // Fetch user data from our API
        try {
          const token = await fbUser.getIdToken()
          const response = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          })

          if (response.ok) {
            const userData = await response.json()
            setUser(userData)
            setNeedsProfile(false)
          } else if (response.status === 404) {
            // User exists in Firebase but not in our DB yet
            // If we have pending signup info (from email signup), register immediately
            if (pendingSignupInfo) {
              const info = pendingSignupInfo
              pendingSignupInfo = null
              const registerResponse = await fetch('/api/auth/register', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                  first_name: info.firstName,
                  last_name: info.lastName,
                  organization: info.organization,
                })
              })

              if (registerResponse.ok) {
                const userData = await registerResponse.json()
                setUser(userData)
                setNeedsProfile(false)
              } else {
                console.error('Failed to register user')
                setUser(null)
              }
            } else {
              // Google sign-in or other — prompt for profile info
              // Pre-fill first/last from displayName if available
              setNeedsProfile(true)
              setUser(null)
            }
          } else {
            setUser(null)
          }
        } catch (err) {
          console.error('Failed to fetch user data:', err)
          setUser(null)
        }
      } else {
        setUser(null)
        setNeedsProfile(false)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [auth])

  const login = async (email: string, password: string) => {
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    }
  }

  const loginWithGoogle = async () => {
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (err: any) {
      setError(err.message || 'Google login failed')
    }
  }

  const signup = async (email: string, password: string, firstName: string, lastName: string, organization: string) => {
    setError(null)
    try {
      // Store signup info so onAuthStateChanged can use it
      pendingSignupInfo = { firstName, lastName, organization }
      await createUserWithEmailAndPassword(auth, email, password)
    } catch (err: any) {
      pendingSignupInfo = null
      setError(err.message || 'Signup failed')
    }
  }

  const completeProfile = async (firstName: string, lastName: string, organization: string) => {
    setError(null)
    if (!firebaseUser) {
      setError('No authenticated user')
      return
    }

    try {
      const token = await firebaseUser.getIdToken()
      const registerResponse = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          organization: organization,
        })
      })

      if (registerResponse.ok) {
        const userData = await registerResponse.json()
        setUser(userData)
        setNeedsProfile(false)
      } else {
        const errorData = await registerResponse.json().catch(() => ({}))
        setError(errorData.error || 'Failed to complete profile')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to complete profile')
    }
  }

  const logout = async () => {
    setError(null)
    try {
      await signOut(auth)
      setUser(null)
      setNeedsProfile(false)
    } catch (err: any) {
      setError(err.message || 'Logout failed')
    }
  }

  const getToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null
    return firebaseUser.getIdToken()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        needsProfile,
        login,
        loginWithGoogle,
        signup,
        completeProfile,
        logout,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
